import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Checkbox, Col, DatePicker, Input, Modal, Radio, Row, Select, Tag, Typography } from "antd";
import moment from "moment";
import { useDispatch } from "react-redux";
import EditableTable from "../components/table/EditableTable";
import useWindowSize from "../hooks/useWindowSize";
import MiFIRSubmissionModal from "./MiFIRSubmissionModal";
import {
  fetchMifirActiveProcess,
  fetchMifirFeedback,
  fetchMifirFeedbackResponses,
  fetchMifirPendingTradingPlatforms,
  fetchMifirSubmissions,
  fetchMifirTransactionsNotSubmitted,
  fetchMifirTransactionsNotSubmittedReportDates,
  prepareMifirReport,
  sendNotification,
} from "../redux/actions";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

// Browser status reads are DB-only, so keep the UI responsive without consuming QuotaGuard.
const MIFIR_STATUS_POLL_INTERVAL_MS = 2000;

const PAGE_CONFIG = {
  transactionsNotSubmitted: {
    title: "Transactions Not Submitted",
    description: "Review pending MiFIR transactions for the current report date (T-1) by trading platform, then prepare one platform at a time.",
  },
  feedback: {
    title: "MiFIR Feedback",
    description: "Review MiFIR feedback over a date range and filter quickly by response status.",
  },
  submissions: {
    title: "MiFIR Submissions",
    description: "Review generated/uploaded reports and start an explicit NCA submission when required.",
  },
};

const HEADER_OVERRIDES = {
  report_number: "Report Number",
  report_date: "Report Date",
  trading_platform: "Trading Platform",
  transaction_id: "Transaction ID",
  transaction_reference_number: "Transaction Reference Number",
  client_id: "Client ID",
  action: "Action",
  response: "Response",
  error_code: "Error Code",
  error_description: "Error Description",
  number_of_transactions: "Number of Transactions",
  generation_error: "Generation Error",
  actions_required: "Actions Required",
  submitted: "Uploaded to SFTP",
  submitted_to_nca: "Submitted to NCA",
  submission_time: "Submission Time",
  creation_time: "Creation Time (UTC)",
  competent_authority_name: "Competent Authority",
  trading_venue_transaction_identification_code: "Trading Venue Transaction Identification Code",
  executing_entity_identification_code: "Executing Entity Identification Code",
  investment_firm_covered_by_directive: "Investment Firm Covered by Directive",
  submitting_entity_identification_code: "Submitting Entity Identification Code",
  buyer_identification_code: "Buyer Identification Code",
  seller_identification_code: "Seller Identification Code",
  buyer_decision_maker_code: "Buyer Decision Maker Code",
  seller_decision_maker_code: "Seller Decision Maker Code",
  instrument_identification_code: "Instrument Identification Code",
  investment_decision_within_firm: "Investment Decision within Firm",
  execution_within_firm: "Execution within Firm",
  short_selling_indicator: "Short Selling Indicator",
  otc_post_trade_indicator: "OTC Post-Trade Indicator",
};

const HEADER_ACRONYMS = {
  id: "ID",
  isin: "ISIN",
  lei: "LEI",
  mic: "MIC",
  nca: "NCA",
  otc: "OTC",
  uti: "UTI",
  utc: "UTC",
};

// Keep dedicated MiFIR pages visually stable when a top-level filter changes.
// In particular, ACPT/RJCT feedback must use the same columns and widths.
const FEEDBACK_COLUMNS = [
  "report_number",
  "report_date",
  "trading_platform",
  "transaction_id",
  "action",
  "response",
  "error_code",
  "error_description",
];

const KNOWN_BOOLEAN_COLUMNS = new Set([
  "submitted",
  "submitted_to_nca",
  "generation_error",
  "error_detected",
  "client_submits",
]);

const cleanHeaderLabel = value => String(value || "")
  .replace(/Identiication/gi, "Identification")
  .replace(/Identificaiton/gi, "Identification")
  .replace(/Identifiction/gi, "Identification");

const makeHeader = name => cleanHeaderLabel(HEADER_OVERRIDES[name] || name
  .split("_")
  .map(part => {
    if (!part) return part;
    const lower = part.toLowerCase();
    return HEADER_ACRONYMS[lower] || (part.charAt(0).toUpperCase() + part.slice(1));
  })
  .join(" "));

const formatReportDate = value => {
  if (!value) return "";
  if (typeof value === "string") return value.substring(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().substring(0, 10);
};

const formatUtcDateTime = (value, showUtcLabel = true) => {
  if (!value) return "";
  const parsed = moment.utc(value);
  if (!parsed.isValid()) return String(value);
  return parsed.format(showUtcLabel ? "YYYY-MM-DD HH:mm:ss [UTC]" : "YYYY-MM-DD HH:mm:ss");
};

const statusColor = status => {
  if (status === "SUCCEEDED") return "green";
  if (["FAILED", "UNKNOWN"].includes(status)) return "red";
  if (status === "ABORTED") return "orange";
  return "blue";
};

const FeedbackResponseRenderer = ({ value }) => {
  if (value === null || typeof value === "undefined" || value === "") return null;
  const response = String(value).toUpperCase();
  const accepted = response === "ACPT";
  return (
    <Tag className="mifir-response-tag" color={accepted ? "green" : "red"}>
      {String(value)}
    </Tag>
  );
};

const defaultFeedbackRange = () => [moment().startOf("month"), moment()];
const feedbackRangeToStrings = range => {
  if (!range || !range[0] || !range[1]) return [null, null];
  return [range[0].format("YYYY-MM-DD"), range[1].format("YYYY-MM-DD")];
};

const MiFIROperationalPage = ({ page }) => {
  const dispatch = useDispatch();
  const gridAPI = useRef(null);
  const tableContainerRef = useRef(null);
  const activeProcessRef = useRef(null);
  const refreshCurrentPageRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [, setLoading] = useState(false);
  const [reportDates, setReportDates] = useState([]);
  const [reportDate, setReportDate] = useState(null);
  const [loadingDates, setLoadingDates] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);
  const [transactionCount, setTransactionCount] = useState(0);
  const [activeProcess, setActiveProcess] = useState(null);
  const [processStatusLoaded, setProcessStatusLoaded] = useState(false);
  const [processActionLoading, setProcessActionLoading] = useState(false);
  const [submitModalVisible, setSubmitModalVisible] = useState(false);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [hasPendingSubmission, setHasPendingSubmission] = useState(false);
  const [pendingSubmissionInfo, setPendingSubmissionInfo] = useState(null);
  const [feedbackAuthorityName, setFeedbackAuthorityName] = useState("NCA");
  const [reprepareModalVisible, setReprepareModalVisible] = useState(false);
  const [reprepareConfirmed, setReprepareConfirmed] = useState(false);
  const [reprepareReason, setReprepareReason] = useState("");
  const [feedbackRange, setFeedbackRange] = useState(defaultFeedbackRange());
  const [feedbackResponse, setFeedbackResponse] = useState("ALL");
  const [feedbackResponses, setFeedbackResponses] = useState([]);
  const [loadingFeedbackResponses, setLoadingFeedbackResponses] = useState(false);
  const [submissionsRange, setSubmissionsRange] = useState(null);
  const [submissionsPlatform, setSubmissionsPlatform] = useState("ALL");
  const [submissionsStatus, setSubmissionsStatus] = useState("ALL");
  const { height } = useWindowSize();

  const pageConfig = PAGE_CONFIG[page] || PAGE_CONFIG.transactionsNotSubmitted;
  const isTransactionsPage = page === "transactionsNotSubmitted";
  const isFeedbackPage = page === "feedback";
  const isSubmissionsPage = page === "submissions";
  const y = tableContainerRef.current ? tableContainerRef.current.offsetTop : 0;
  const tableHeight = Math.max(360, height - y - 90);

  // Feedback is read-only and must remain fully usable while preparation/submission runs.
  const operationalControlsDisabled = !processStatusLoaded || Boolean(activeProcess) || processActionLoading;
  const controlsDisabled = isFeedbackPage ? false : operationalControlsDisabled;

  const selectedPlatformInfo = platforms.find(item => item.trading_platform === selectedPlatform);

  const submissionPlatformOptions = useMemo(() => Array.from(new Set(
    rows.map(row => row.trading_platform).filter(Boolean)
  )).sort(), [rows]);

  const visibleRows = useMemo(() => {
    if (!isSubmissionsPage) return rows;

    return rows.filter(row => {
      if (submissionsPlatform !== "ALL" && row.trading_platform !== submissionsPlatform) return false;

      if (submissionsRange && submissionsRange[0] && submissionsRange[1]) {
        const rowDate = moment(formatReportDate(row.report_date), "YYYY-MM-DD", true);
        if (!rowDate.isValid()) return false;
        if (rowDate.isBefore(submissionsRange[0], "day") || rowDate.isAfter(submissionsRange[1], "day")) return false;
      }

      const noTrades = Number(row.report_number) === -1;
      const submitted = Boolean(row.submitted) || Boolean(row.submitted_to_nca);
      if (submissionsStatus === "SUBMITTED" && !submitted) return false;
      if (submissionsStatus === "PENDING" && (submitted || noTrades)) return false;
      if (submissionsStatus === "NO_TRADES" && !noTrades) return false;

      return true;
    });
  }, [rows, isSubmissionsPage, submissionsRange, submissionsPlatform, submissionsStatus]);

  const columnDefs = useMemo(() => columns.map(name => {
    const populatedValues = rows
      .map(row => row[name])
      .filter(value => value !== null && typeof value !== "undefined");
    const isBooleanColumn = KNOWN_BOOLEAN_COLUMNS.has(name) || (
      populatedValues.length > 0
      && populatedValues.every(value => typeof value === "boolean")
    );
    const isLongTextColumn = ["error_description", "actions_required", "message"].includes(name);

    const compactWidths = {
      report_number: isTransactionsPage ? 125 : 105,
      report_date: isTransactionsPage ? 125 : 105,
      trading_platform: isTransactionsPage ? 145 : 115,
      transaction_id: isTransactionsPage ? 220 : 150,
      action: 90,
      response: 90,
      error_code: 105,
      competent_authority_name: 125,
      submission_filename: 170,
      submission_time: 145,
      number_of_transactions: 120,
      generation_error: 105,
      submitted_to_nca: 105,
      trading_venue_transaction_identification_code: 220,
      executing_entity_identification_code: 190,
      investment_firm_covered_by_directive: 180,
      submitting_entity_identification_code: 195,
      buyer_identification_code: 185,
      seller_identification_code: 185,
      buyer_decision_maker_code: 190,
      seller_decision_maker_code: 190,
      instrument_identification_code: 195,
      investment_decision_within_firm: 175,
      execution_within_firm: 165,
      short_selling_indicator: 145,
      otc_post_trade_indicator: 155,
    };

    return {
      headerName: makeHeader(name),
      field: name,
      editable: false,
      sortable: true,
      filter: true,
      resizable: true,
      cellRenderer: isBooleanColumn ? "booleanRenderer" : undefined,
      cellRendererFramework: isFeedbackPage && name === "response"
        ? FeedbackResponseRenderer
        : undefined,
      cellRendererParams: isBooleanColumn
        ? (isTransactionsPage
          ? { neutral: true }
          : (["generation_error", "error_detected"].includes(name) ? { inverse: true } : undefined))
        : undefined,
      valueFormatter: name === "report_date"
        ? params => formatReportDate(params.value)
        : (name === "submission_time"
          ? params => formatUtcDateTime(params.value, false)
          : (name === "creation_time"
            ? params => formatUtcDateTime(params.value, true)
            : (name === "report_number"
            ? params => (Number(params.value) === -1 ? "No Reportable Trades" : params.value)
            : undefined))),
      cellClass: [
        isBooleanColumn ? "mifir-boolean-cell" : null,
        isLongTextColumn ? "mifir-long-text-cell" : "mifir-center-cell",
      ].filter(Boolean).join(" ") || undefined,
      wrapText: isLongTextColumn,
      autoHeight: isLongTextColumn,
      tooltipField: isLongTextColumn ? name : undefined,
      flex: undefined,
      minWidth: isTransactionsPage ? (isLongTextColumn ? 300 : 105) : (isLongTextColumn ? 210 : 70),
      maxWidth: isLongTextColumn ? (isTransactionsPage ? 560 : 440) : undefined,
      width: isLongTextColumn
        ? (isTransactionsPage
          ? 420
          : (name === "error_description" ? 340 : (name === "actions_required" ? 240 : 300)))
        : (isBooleanColumn ? (isTransactionsPage ? 130 : 95) : (compactWidths[name] || (isTransactionsPage ? 155 : 110))),
    };
  }), [columns, rows, isTransactionsPage, isFeedbackPage]);

  const setGridData = useCallback(payload => {
    const resultRows = (payload && payload.rows) || [];
    const resultColumns = (payload && payload.columns) || [];
    const visibleColumns = page === "submissions"
      ? resultColumns.filter(name => name !== "client_submits")
      : (page === "feedback"
        ? FEEDBACK_COLUMNS
        : resultColumns);
    setColumns(visibleColumns);
    setRows(resultRows.map((row, index) => ({ ...row, _rowId: `${page}-${index}` })));
    setTransactionCount(isTransactionsPage ? ((payload && payload.count) || resultRows.length) : 0);
    setHasPendingSubmission(page === "submissions" && Boolean(
      payload && (typeof payload.canSubmitPendingCurrentReport === "boolean"
        ? payload.canSubmitPendingCurrentReport
        : (payload.hasPendingCurrentReport && !(payload.pendingCurrentReport && payload.pendingCurrentReport.generation_error)))
    ));
    if (page === "submissions") {
      setPendingSubmissionInfo((payload && payload.pendingCurrentReport) || null);
    }
    if (page === "feedback") {
      setFeedbackAuthorityName((payload && payload.competentAuthorityName) || "NCA");
    }
  }, [isTransactionsPage, page]);

  const loadStandardData = useCallback(async (
    selectedReportDate = reportDate,
    tradingPlatform = selectedPlatform,
    silent = false
  ) => {
    if (isTransactionsPage && (!selectedReportDate || !tradingPlatform)) {
      setRows([]);
      setColumns([]);
      setTransactionCount(0);
      return;
    }

    if (!silent) setLoading(true);
    if (!silent && gridAPI.current) gridAPI.current.showLoadingOverlay();

    try {
      const action = isTransactionsPage
        ? fetchMifirTransactionsNotSubmitted(selectedReportDate, tradingPlatform)
        : fetchMifirSubmissions();
      const { payload } = await dispatch(action);
      setGridData(payload);
    } catch (err) {
      if (!silent) {
        setRows([]);
        setTransactionCount(0);
        dispatch(sendNotification({
          type: "error",
          message: "MiFIR Data",
          description: "Could not load MiFIR data.",
        }));
      }
    } finally {
      if (!silent) setLoading(false);
      if (!silent && gridAPI.current) gridAPI.current.hideOverlay();
    }
  }, [dispatch, isTransactionsPage, reportDate, selectedPlatform, setGridData]);

  const loadFeedbackData = useCallback(async (
    range = feedbackRange,
    response = feedbackResponse,
    silent = false
  ) => {
    const [from, to] = feedbackRangeToStrings(range);
    if (!from || !to) {
      setRows([]);
      setColumns([]);
      return;
    }

    if (!silent) setLoading(true);
    if (!silent && gridAPI.current) gridAPI.current.showLoadingOverlay();
    try {
      const { payload } = await dispatch(fetchMifirFeedback(from, to, response || "ALL"));
      setGridData(payload);
    } catch (err) {
      if (!silent) {
        setRows([]);
        dispatch(sendNotification({
          type: "error",
          message: "MiFIR Feedback",
          description: "Could not load MiFIR feedback.",
        }));
      }
    } finally {
      if (!silent) setLoading(false);
      if (!silent && gridAPI.current) gridAPI.current.hideOverlay();
    }
  }, [dispatch, feedbackRange, feedbackResponse, setGridData]);

  const loadFeedbackResponseOptions = useCallback(async (
    range = feedbackRange,
    preferredResponse = feedbackResponse
  ) => {
    const [from, to] = feedbackRangeToStrings(range);
    if (!from || !to) {
      setFeedbackResponses([]);
      setFeedbackResponse("ALL");
      return "ALL";
    }

    setLoadingFeedbackResponses(true);
    try {
      const { payload } = await dispatch(fetchMifirFeedbackResponses(from, to));
      const responses = payload || [];
      setFeedbackResponses(responses);
      const selected = preferredResponse === "ALL" || responses.includes(preferredResponse)
        ? preferredResponse
        : "ALL";
      setFeedbackResponse(selected);
      return selected;
    } catch (err) {
      setFeedbackResponses([]);
      setFeedbackResponse("ALL");
      return "ALL";
    } finally {
      setLoadingFeedbackResponses(false);
    }
  }, [dispatch, feedbackRange, feedbackResponse]);

  const loadPlatforms = useCallback(async (date, preferredPlatform = selectedPlatform, silent = false) => {
    if (!date) {
      setPlatforms([]);
      setSelectedPlatform(null);
      setRows([]);
      setTransactionCount(0);
      return null;
    }

    if (!silent) setLoadingPlatforms(true);
    try {
      const { payload } = await dispatch(fetchMifirPendingTradingPlatforms(date));
      const result = payload || [];
      setPlatforms(result);

      const preferredStillExists = result.some(
        item => item.trading_platform === preferredPlatform && !item.submission_final
      );
      const firstActionablePlatform = result.find(item => !item.submission_final);
      const chosen = preferredStillExists
        ? preferredPlatform
        : (firstActionablePlatform ? firstActionablePlatform.trading_platform : null);

      setSelectedPlatform(chosen);
      if (chosen) await loadStandardData(date, chosen, silent);
      else {
        setRows([]);
        setColumns([]);
        setTransactionCount(0);
      }
      return chosen;
    } catch (err) {
      if (!silent) {
        dispatch(sendNotification({
          type: "error",
          message: "MiFIR Trading Platforms",
          description: "Could not load the customer's trading platforms.",
        }));
      }
      setPlatforms([]);
      setSelectedPlatform(null);
      setRows([]);
      setTransactionCount(0);
      return null;
    } finally {
      if (!silent) setLoadingPlatforms(false);
    }
  }, [dispatch, loadStandardData, selectedPlatform]);

  const loadReportDates = useCallback(async () => {
    setLoadingDates(true);
    try {
      const { payload } = await dispatch(fetchMifirTransactionsNotSubmittedReportDates());
      const dates = payload || [];
      setReportDates(dates);
      const latestDate = dates.length ? dates[0] : null;
      setReportDate(latestDate);

      if (latestDate) await loadPlatforms(latestDate, null);
      else {
        setPlatforms([]);
        setSelectedPlatform(null);
        setRows([]);
        setColumns([]);
        setTransactionCount(0);
      }
    } catch (err) {
      setReportDates([]);
      setReportDate(null);
      setPlatforms([]);
      setSelectedPlatform(null);
      setTransactionCount(0);
    } finally {
      setLoadingDates(false);
    }
  }, [dispatch, loadPlatforms]);

  const refreshCurrentPage = useCallback(async (silent = false) => {
    if (isTransactionsPage) {
      await loadPlatforms(reportDate, selectedPlatform, silent);
    } else if (isFeedbackPage) {
      const response = await loadFeedbackResponseOptions(feedbackRange, feedbackResponse);
      await loadFeedbackData(feedbackRange, response, silent);
    } else {
      await loadStandardData(null, null, silent);
    }
  }, [
    feedbackRange,
    feedbackResponse,
    isFeedbackPage,
    isTransactionsPage,
    loadFeedbackData,
    loadFeedbackResponseOptions,
    loadPlatforms,
    loadStandardData,
    reportDate,
    selectedPlatform,
  ]);

  refreshCurrentPageRef.current = refreshCurrentPage;

  useEffect(() => {
    setRows([]);
    setColumns([]);
    setReportDates([]);
    setReportDate(null);
    setPlatforms([]);
    setSelectedPlatform(null);
    setTransactionCount(0);
    setHasPendingSubmission(false);
    setPendingSubmissionInfo(null);
    setFeedbackAuthorityName("NCA");

    if (isTransactionsPage) {
      loadReportDates();
      return;
    }

    if (isFeedbackPage) {
      const range = defaultFeedbackRange();
      setFeedbackRange(range);
      setFeedbackResponse("ALL");
      Promise.resolve(loadFeedbackResponseOptions(range, "ALL"))
        .then(response => loadFeedbackData(range, response));
      return;
    }

    loadStandardData();
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let mounted = true;
    let inFlight = false;

    const poll = async () => {
      if (!mounted || inFlight) return;
      inFlight = true;
      try {
        const { payload } = await dispatch(fetchMifirActiveProcess());
        if (!mounted) return;
        const wasActive = Boolean(activeProcessRef.current);
        const nowActive = Boolean(payload);
        activeProcessRef.current = payload || null;
        setActiveProcess(payload || null);
        setProcessStatusLoaded(true);

        // Refresh the current Daily Reporting page as soon as the live process
        // disappears. Use a ref so changing filters/platforms cannot tear down
        // and recreate the process-status polling timer.
        if (wasActive && !nowActive && refreshCurrentPageRef.current) {
          await refreshCurrentPageRef.current(true);
        }
      } catch (err) {
        // Status polling is deliberately silent; the next poll will retry.
      } finally {
        inFlight = false;
      }
    };

    let timer = null;

    const scheduleNextPoll = () => {
      if (!mounted) return;
      timer = setTimeout(async () => {
        await poll();
        scheduleNextPoll();
      }, MIFIR_STATUS_POLL_INTERVAL_MS);
    };

    Promise.resolve(poll()).finally(scheduleNextPoll);
    const onFocus = () => poll();
    const onVisibility = () => {
      if (document.visibilityState === "visible") poll();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      mounted = false;
      clearTimeout(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dispatch]);

  const onPlatformChange = async value => {
    setSelectedPlatform(value);
    await loadStandardData(reportDate, value);
  };

  const onFeedbackRangeChange = async value => {
    if (!value || !value[0] || !value[1]) return;
    setFeedbackRange(value);
    const response = await loadFeedbackResponseOptions(value, feedbackResponse);
    await loadFeedbackData(value, response);
  };

  const onFeedbackResponseChange = async value => {
    const selected = value || "ALL";
    setFeedbackResponse(selected);
    await loadFeedbackData(feedbackRange, selected);
  };

  const startPreparation = async (forceReprepare = false, reason = "") => {
    if (!reportDate || !selectedPlatform) return;
    setProcessActionLoading(true);
    try {
      const { payload } = await dispatch(prepareMifirReport(
        reportDate,
        selectedPlatform,
        forceReprepare,
        reason
      ));
      activeProcessRef.current = payload || null;
      setActiveProcess(payload || null);
      setReprepareModalVisible(false);
      setReprepareConfirmed(false);
      setReprepareReason("");
      dispatch(sendNotification({
        type: "success",
        message: forceReprepare ? "MiFIR Report Repreparation" : "MiFIR Report Preparation",
        description: `${forceReprepare ? "Repreparation" : "Preparation"} started for ${selectedPlatform}.`,
      }));
    } catch (err) {
      if (!forceReprepare && err && err.code === "REPORT_ALREADY_PREPARED") {
        setReprepareConfirmed(false);
        setReprepareReason("");
        setReprepareModalVisible(true);
        return;
      }
      if (!forceReprepare && err && err.code === "REPORT_HAS_GENERATION_ERROR") {
        dispatch(sendNotification({
          type: "warning",
          message: "Generation Error",
          description: "The existing prepared report contains generation errors. Reprepare is available without the logical-error verification step.",
        }));
        await loadPlatforms(reportDate, selectedPlatform, true);
        return;
      }
      if (!forceReprepare && err && err.code === "REPORT_HAS_WARNINGS") {
        dispatch(sendNotification({
          type: "warning",
          message: "Warnings Detected",
          description: "The prepared report contains warnings. Warnings do not prevent submission; reprepare only if you choose to correct them.",
        }));
        await loadPlatforms(reportDate, selectedPlatform, true);
        return;
      }
      dispatch(sendNotification({
        type: "error",
        message: forceReprepare ? "MiFIR Report Repreparation" : "MiFIR Report Preparation",
        description: (err && err.error) || `Could not start report ${forceReprepare ? "repreparation" : "preparation"}.`,
      }));
    } finally {
      setProcessActionLoading(false);
    }
  };

  const onPrepareReport = async () => {
    if (!reportDate || !selectedPlatform) return;
    if (selectedPlatformInfo && selectedPlatformInfo.submission_final) return;
    if (selectedPlatformInfo && selectedPlatformInfo.no_reportable_transactions) {
      // No Reportable Trades is not a terminal state. The source/database may
      // have been corrected since the previous run, so allow a direct re-run.
      await startPreparation(true, "");
      return;
    }
    if (selectedPlatformInfo && selectedPlatformInfo.prepared) {
      if (selectedPlatformInfo.blocking_generation_error) {
        await startPreparation(true, "Generation error detected in prepared report.");
        return;
      }
      if (selectedPlatformInfo.has_warnings) {
        await startPreparation(true, "Warnings detected in prepared report.");
        return;
      }
      // Backward-compatible fallback while older API responses are being replaced.
      if (selectedPlatformInfo.generation_error
        && selectedPlatformInfo.blocking_generation_error === undefined
        && selectedPlatformInfo.has_warnings === undefined) {
        await startPreparation(true, "Generation issue detected in prepared report.");
        return;
      }
      setReprepareConfirmed(false);
      setReprepareReason("");
      setReprepareModalVisible(true);
      return;
    }
    await startPreparation(false, "");
  };

  const onConfirmReprepare = async () => {
    const reason = reprepareReason.trim();
    if (!reprepareConfirmed || reason.length < 10) return;
    await startPreparation(true, reason);
  };

  const exportCSV = () => {
    if (!gridAPI.current) return;
    const [feedbackFrom, feedbackTo] = feedbackRangeToStrings(feedbackRange);
    const datePart = isFeedbackPage
      ? `${feedbackFrom || ""}-${feedbackTo || ""}`
      : (reportDate || "");
    gridAPI.current.exportDataAsCsv({
      columnKeys: columns,
      fileName: `${page}${datePart ? `-${datePart}` : ""}${selectedPlatform ? `-${selectedPlatform}` : ""}${isFeedbackPage && feedbackResponse !== "ALL" ? `-${feedbackResponse}` : ""}.csv`,
    });
  };

  return (
    <div className="mifir-operational-page">
      <div className="mifir-page-heading">
        <div>
          <h2>{pageConfig.title}</h2>
          <Text type="secondary">{pageConfig.description}</Text>
        </div>
        {isTransactionsPage && selectedPlatform && (
          <div className="mifir-summary-badge">
            <span className="mifir-summary-badge__label">Pending transactions</span>
            <strong>{transactionCount.toLocaleString()}</strong>
          </div>
        )}
      </div>

      {activeProcess && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={(
            <span>
              MiFIR process running: <strong>{activeProcess.is_repreparation ? "REPREPARATION" : activeProcess.process_type}</strong>{" "}
              <Tag color={statusColor(activeProcess.status)}>{activeProcess.status === "SUCCEEDED" ? "SUCCESSFUL" : activeProcess.status}</Tag>
            </span>
          )}
          description={[
            activeProcess.report_date ? `Report date: ${formatReportDate(activeProcess.report_date)}` : null,
            activeProcess.trading_platform ? `Platform: ${activeProcess.trading_platform}` : null,
            activeProcess.run_id ? `Run ID: ${activeProcess.run_id}` : null,
            activeProcess.progress_percent !== null && typeof activeProcess.progress_percent !== "undefined" && Number.isFinite(Number(activeProcess.progress_percent)) ? `Progress: ${Number(activeProcess.progress_percent)}%` : null,
            activeProcess.message || null,
          ].filter(Boolean).join(" | ")}
        />
      )}

      <div className="mifir-toolbar">
        <Row className="mifir-toolbar-row" gutter={[10, 6]} align="middle">
          {isTransactionsPage && (
            <Col>
              <div className="mifir-filter-field">
                <Text className="mifir-filter-label">Report Date (T-1)</Text>
                <span className="mifir-current-report-date">
                  {loadingDates ? "Loading..." : (reportDate || "—")}
                </span>
              </div>
            </Col>
          )}

          {isFeedbackPage && (
            <>
              <Col>
                <div className="mifir-filter-field">
                  <Text className="mifir-filter-label">Report Date Range</Text>
                  <RangePicker
                    className="mifir-filter-range"
                    value={feedbackRange}
                    onChange={onFeedbackRangeChange}
                    format="YYYY-MM-DD"
                    allowClear={false}
                    disabled={false}
                  />
                </div>
              </Col>
              <Col>
                <div className="mifir-filter-field">
                  <Text className="mifir-filter-label">Response</Text>
                  <Select
                    className="mifir-filter-select"
                    value={feedbackResponse}
                    onChange={onFeedbackResponseChange}
                    loading={loadingFeedbackResponses}
                    disabled={false}
                  >
                    <Option value="ALL">All</Option>
                    {feedbackResponses.map(value => <Option key={value} value={value}>{value}</Option>)}
                  </Select>
                </div>
              </Col>
            </>
          )}

          {isSubmissionsPage && (
            <>
              <Col>
                <div className="mifir-filter-field">
                  <Text className="mifir-filter-label">Report Date Range</Text>
                  <RangePicker
                    className="mifir-filter-range"
                    value={submissionsRange}
                    onChange={setSubmissionsRange}
                    format="YYYY-MM-DD"
                    allowClear
                  />
                </div>
              </Col>
              <Col>
                <div className="mifir-filter-field">
                  <Text className="mifir-filter-label">Trading Platform</Text>
                  <Select
                    className="mifir-filter-select"
                    value={submissionsPlatform}
                    onChange={setSubmissionsPlatform}
                  >
                    <Option value="ALL">All</Option>
                    {submissionPlatformOptions.map(value => <Option key={value} value={value}>{value}</Option>)}
                  </Select>
                </div>
              </Col>
              <Col>
                <div className="mifir-filter-field">
                  <Text className="mifir-filter-label">Status</Text>
                  <Select
                    className="mifir-filter-select"
                    value={submissionsStatus}
                    onChange={setSubmissionsStatus}
                  >
                    <Option value="ALL">All</Option>
                    <Option value="PENDING">Pending</Option>
                    <Option value="SUBMITTED">Uploaded to SFTP</Option>
                    <Option value="NO_TRADES">No Reportable Trades</Option>
                  </Select>
                </div>
              </Col>
            </>
          )}

          <Col>
            <div className="mifir-toolbar-actions">
              <Button
                type="primary"
                onClick={exportCSV}
                disabled={(isFeedbackPage ? false : controlsDisabled) || !visibleRows.length}
              >
                Export CSV
              </Button>

              {isFeedbackPage && (
                <Button
                  type="primary"
                  onClick={() => setFeedbackModalVisible(true)}
                  disabled={operationalControlsDisabled}
                >
                  Receive Feedback
                </Button>
              )}

              {isTransactionsPage && (
                <Button
                  type="primary"
                  onClick={onPrepareReport}
                  loading={processActionLoading}
                  disabled={
                    controlsDisabled
                    || !reportDate
                    || !selectedPlatform
                    || Boolean(selectedPlatformInfo && selectedPlatformInfo.submission_final)
                  }
                >
                  {selectedPlatformInfo && selectedPlatformInfo.no_reportable_transactions
                    ? "Reprepare Report"
                    : (selectedPlatformInfo && selectedPlatformInfo.prepared ? "Reprepare Report" : "Prepare Report")}
                </Button>
              )}

              {page === "submissions" && (
                <Button
                  type="primary"
                  onClick={() => setSubmitModalVisible(true)}
                  disabled={controlsDisabled || !hasPendingSubmission}
                >
                  Submit
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </div>

      {isTransactionsPage && reportDate && (
        <div className="mifir-platform-section">
          <div className="mifir-platform-section__title">
            <Text strong>Trading Platform</Text>
            {selectedPlatformInfo && selectedPlatformInfo.counterparty_name && (
              <Tag className="mifir-counterparty-tag">Counterparty: {selectedPlatformInfo.counterparty_name}</Tag>
            )}
          </div>
          {loadingPlatforms ? (
            <Text type="secondary">Loading platforms...</Text>
          ) : platforms.length ? (
            <Radio.Group
              className="mifir-platform-group"
              value={selectedPlatform}
              onChange={event => onPlatformChange(event.target.value)}
              disabled={controlsDisabled}
              buttonStyle="solid"
            >
              {platforms.map(platform => (
                <Radio.Button
                  key={platform.trading_platform}
                  value={platform.trading_platform}
                  className={`mifir-platform-card${platform.submission_final ? " mifir-platform-card--final" : ""}`}
                  disabled={Boolean(platform.submission_final)}
                >
                  <div className="mifir-platform-card__name">{platform.trading_platform}</div>
                  <div className="mifir-platform-card__count">
                    {platform.submitted_for_day
                      ? `${Number(platform.submitted_transaction_count || 0).toLocaleString()} transactions submitted · ${Number(platform.transaction_count || 0).toLocaleString()} pending`
                      : `${Number(platform.transaction_count || 0).toLocaleString()} transactions`}
                  </div>
                  {platform.submission_final && (
                    <div className="mifir-platform-card__final">Submitted · Final for the day</div>
                  )}
                  {!platform.submission_final && platform.submitted_for_day && (
                    <div className="mifir-platform-card__prepared">Submitted · Further preparation allowed</div>
                  )}
                  {!platform.submitted_for_day && platform.no_reportable_transactions && (
                    <div className="mifir-platform-card__prepared">No Reportable Trades · Reprepare available</div>
                  )}
                  {!platform.submitted_for_day && !platform.no_reportable_transactions && platform.prepared && (
                    <div className={`mifir-platform-card__prepared${platform.blocking_generation_error ? " mifir-platform-card__prepared--error" : ""}`}>
                      {platform.blocking_generation_error
                        ? "Prepared · Generation error · Reprepare available"
                        : (platform.has_warnings
                          ? "Prepared · Warnings · Submission allowed"
                          : "Prepared · Reprepare available")}
                    </div>
                  )}
                </Radio.Button>
              ))}
            </Radio.Group>
          ) : (
            <Text type="secondary">No trading platforms are available for the current report date.</Text>
          )}
          {selectedPlatformInfo && selectedPlatformInfo.no_reportable_transactions && (
            <Alert
              style={{ marginTop: 8 }}
              type="info"
              showIcon
              message="No Reportable Trades"
              description="The previous preparation found no reportable trades. There is no report waiting for submission, but you can choose Reprepare Report after correcting the underlying data to run the report again."
            />
          )}
          {selectedPlatformInfo && !selectedPlatformInfo.no_reportable_transactions && selectedPlatformInfo.prepared && selectedPlatformInfo.blocking_generation_error && (
            <Alert
              style={{ marginTop: 8 }}
              type="warning"
              showIcon
              message="The prepared report contains generation errors."
              description="The preparation itself is considered completed. You can reprepare the report directly after reviewing/correcting the generation issue."
            />
          )}
          {selectedPlatformInfo && !selectedPlatformInfo.no_reportable_transactions && selectedPlatformInfo.prepared && !selectedPlatformInfo.blocking_generation_error && selectedPlatformInfo.has_warnings && (
            <Alert
              style={{ marginTop: 8 }}
              type="warning"
              showIcon
              message="The prepared report contains warnings."
              description="Warnings do not prevent submission. You may submit the report as-is. If you choose to correct a warning, review/correct it and then use Reprepare Report to generate the report again."
            />
          )}
          {selectedPlatformInfo && !selectedPlatformInfo.no_reportable_transactions && selectedPlatformInfo.prepared && !selectedPlatformInfo.blocking_generation_error && !selectedPlatformInfo.has_warnings && (
            <Alert
              style={{ marginTop: 8 }}
              type="warning"
              showIcon
              message="A valid report is already prepared and waiting for submission."
              description="You may submit the existing report, or choose Reprepare Report when a logical/data issue has been identified that automated validation cannot detect. Repreparation requires explicit confirmation and a reason."
            />
          )}
          {selectedPlatformInfo
            && !selectedPlatformInfo.submitted_for_day
            && !selectedPlatformInfo.no_reportable_transactions
            && !selectedPlatformInfo.prepared
            && !activeProcess && (
            <Alert
              style={{ marginTop: 8 }}
              type="info"
              showIcon
              message="Report preparation required"
              description="No prepared MiFIR result exists yet for this report date and trading platform. Select Prepare Report to run report preparation."
            />
          )}
        </div>
      )}

      {isTransactionsPage && !loadingDates && !reportDates.length && (
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary">No report dates are available for this user.</Text>
        </div>
      )}

      <div ref={tableContainerRef} className={`mifir-grid-card${isTransactionsPage ? " mifir-grid-card--horizontal" : " mifir-grid-card--fit"}`}>
        <EditableTable
          columnDefs={columnDefs}
          defaultColDefs={{ sortable: true, filter: true, resizable: true }}
          rowData={visibleRows}
          setAPI={api => { gridAPI.current = api; }}
          getRowNodeId={data => data._rowId}
          height={tableHeight}
          headerHeight={isTransactionsPage ? 82 : 56}
          rowHeight={isFeedbackPage ? 34 : (isSubmissionsPage ? 40 : 34)}
          fitColumnsToWidth={!isTransactionsPage}
          suppressHorizontalScroll={!isTransactionsPage}
        />
      </div>

      {isTransactionsPage && (
        <Modal
          visible={reprepareModalVisible}
          title="Confirm Report Repreparation"
          okText="Confirm & Reprepare"
          cancelText="Cancel"
          confirmLoading={processActionLoading}
          okButtonProps={{
            disabled: !reprepareConfirmed || reprepareReason.trim().length < 10 || controlsDisabled,
          }}
          onOk={onConfirmReprepare}
          onCancel={() => {
            if (processActionLoading) return;
            setReprepareModalVisible(false);
            setReprepareConfirmed(false);
            setReprepareReason("");
          }}
          maskClosable={!processActionLoading}
          closable={!processActionLoading}
          destroyOnClose
          width={620}
        >
          <Alert
            type="warning"
            showIcon
            message="The current report is considered valid by automated validation."
            description="Reprepare only when a logical or data issue has been identified that the validator cannot detect. This will start report preparation again for the same customer, report date and trading platform."
            style={{ marginBottom: 16 }}
          />

          <div className="mifir-reprepare-summary">
            <div><Text type="secondary">Counterparty</Text><strong>{selectedPlatformInfo && selectedPlatformInfo.counterparty_name}</strong></div>
            <div><Text type="secondary">Report Date</Text><strong>{reportDate}</strong></div>
            <div><Text type="secondary">Trading Platform</Text><strong>{selectedPlatform}</strong></div>
            <div><Text type="secondary">Pending Transactions</Text><strong>{transactionCount.toLocaleString()}</strong></div>
          </div>

          <div className="mifir-reprepare-field">
            <Text strong>Reason for repreparation</Text>
            <Text type="secondary" className="mifir-reprepare-help">
              Describe the logical/data issue or correction that requires the report to be generated again.
            </Text>
            <Input.TextArea
              value={reprepareReason}
              onChange={event => setReprepareReason(event.target.value)}
              placeholder="Example: corrected mapping identified after validation; the automated validator cannot detect this logical issue."
              rows={4}
              maxLength={1000}
              disabled={processActionLoading}
            />
            <Text type={reprepareReason.trim().length > 0 && reprepareReason.trim().length < 10 ? "danger" : "secondary"}>
              {reprepareReason.trim().length}/1000 characters · minimum 10 characters
            </Text>
          </div>

          <Checkbox
            checked={reprepareConfirmed}
            onChange={event => setReprepareConfirmed(event.target.checked)}
            disabled={processActionLoading}
          >
            I confirm that the existing report passed automated validation, but must be prepared again because of a logical/data issue that validation cannot identify.
          </Checkbox>
        </Modal>
      )}

      {isFeedbackPage && (
        <MiFIRSubmissionModal
          mode="feedback"
          authorityName={feedbackAuthorityName}
          visible={feedbackModalVisible}
          disabled={operationalControlsDisabled}
          onClose={() => setFeedbackModalVisible(false)}
          onStarted={process => {
            activeProcessRef.current = process || null;
            setActiveProcess(process || null);
          }}
        />
      )}

      {page === "submissions" && (
        <MiFIRSubmissionModal
          authorityName={(pendingSubmissionInfo && pendingSubmissionInfo.competent_authority_name) || "NCA"}
          visible={submitModalVisible}
          disabled={controlsDisabled || !hasPendingSubmission}
          onClose={() => setSubmitModalVisible(false)}
          onStarted={process => {
            activeProcessRef.current = process || null;
            setActiveProcess(process || null);
          }}
        />
      )}
    </div>
  );
};

export default MiFIROperationalPage;
